import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { PublicHeader } from "@/components/PublicHeader";
import { Seo } from "@/components/Seo";
import { RecruitmentFallback } from "@/components/RecruitmentFallback";
import { AdaptiveMediaFrame } from "@/components/AdaptiveMediaFrame";
import { ShareDialog } from "@/components/ShareDialog";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { publicSiteUrl } from "@/lib/share";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  Briefcase, MapPin, Building2, BadgeCheck, ShieldCheck, Loader2, ArrowLeft, Share2,
  Wallet, Send, MessageCircle, Upload, CheckCircle2,
} from "lucide-react";

function ApplyDialog({ open, onOpenChange, rid, isAuthed, onApplied }) {
  const [form, setForm] = useState({ full_name: "", email: "", mobile: "", cover_note: "" });
  const [cv, setCv] = useState(null);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!consent) { toast.error("Please provide consent to submit your application"); return; }
    if (!isAuthed && (!form.full_name.trim() || !form.email.trim())) { toast.error("Full name and email are required"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("full_name", form.full_name);
      fd.append("email", form.email);
      fd.append("mobile", form.mobile);
      fd.append("cover_note", form.cover_note);
      fd.append("consent", "true");
      if (cv) fd.append("cv", cv);
      await api.post(`/public/recruitment/${rid}/apply`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Application submitted");
      onOpenChange(false);
      onApplied && onApplied();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-2xl" data-testid="recruitment-apply-dialog">
        <DialogHeader>
          <DialogTitle>Apply for this opportunity</DialogTitle>
          <DialogDescription>{isAuthed ? "We'll share your PropertyConnect profile details with the recruiter." : "Share your details with the recruiter."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {!isAuthed && (
            <>
              <div><Label>Full name *</Label><Input data-testid="apply-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="rounded-xl mt-1" /></div>
              <div><Label>Email *</Label><Input data-testid="apply-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl mt-1" /></div>
              <div><Label>Mobile</Label><Input data-testid="apply-mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="rounded-xl mt-1" /></div>
            </>
          )}
          <div><Label>Cover note</Label><Textarea data-testid="apply-cover-note" value={form.cover_note} onChange={(e) => setForm({ ...form, cover_note: e.target.value })} rows={3} className="rounded-xl mt-1" placeholder="Tell the recruiter why you're a great fit" /></div>
          <div>
            <Label>CV / Resume (optional, PDF/JPG/PNG, max 15 MB)</Label>
            <label className="mt-1 flex items-center gap-2 rounded-xl border border-dashed border-slate-300 px-3 py-2.5 cursor-pointer hover:border-[#0F2C59]/40" data-testid="apply-cv-label">
              <Upload className="h-4 w-4 text-slate-400" />
              <span className="text-sm text-slate-500 truncate">{cv ? cv.name : "Attach your CV"}</span>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" data-testid="apply-cv-input" onChange={(e) => setCv(e.target.files?.[0] || null)} />
            </label>
          </div>
          <label className="flex items-start gap-2 text-sm text-slate-600">
            <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} data-testid="apply-consent" className="mt-0.5" />
            <span>I consent to sharing my details and CV with this recruiter for the purpose of this application.</span>
          </label>
        </div>
        <DialogFooter>
          <Button data-testid="apply-submit" onClick={submit} disabled={busy} className="rounded-xl bg-[#0F2C59] hover:bg-[#0F2C59]/90">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1.5" /> Submit application</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AskDialog({ open, onOpenChange, rid, isAuthed }) {
  const [form, setForm] = useState({ full_name: "", email: "", mobile: "", message: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.message.trim()) { toast.error("Please enter a message"); return; }
    if (!isAuthed && (!form.full_name.trim() || !form.email.trim())) { toast.error("Full name and email are required"); return; }
    setBusy(true);
    try {
      await api.post(`/public/recruitment/${rid}/inquiries`, form);
      toast.success("Message sent to the recruiter");
      onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-2xl" data-testid="recruitment-ask-dialog">
        <DialogHeader>
          <DialogTitle>Ask about this opportunity</DialogTitle>
          <DialogDescription>Send a question to the recruiter.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {!isAuthed && (
            <>
              <div><Label>Full name *</Label><Input data-testid="ask-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="rounded-xl mt-1" /></div>
              <div><Label>Email *</Label><Input data-testid="ask-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl mt-1" /></div>
            </>
          )}
          <div><Label>Message *</Label><Textarea data-testid="ask-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4} className="rounded-xl mt-1" /></div>
        </div>
        <DialogFooter>
          <Button data-testid="ask-submit" onClick={submit} disabled={busy} className="rounded-xl bg-[#0F2C59] hover:bg-[#0F2C59]/90">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send message"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function RecruitmentDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [r, setR] = useState(undefined);
  const [applyOpen, setApplyOpen] = useState(false);
  const [askOpen, setAskOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    setR(undefined);
    api.get(`/public/recruitment/${id}`)
      .then(({ data }) => alive && setR(data))
      .catch(() => alive && setR(null));
    return () => { alive = false; };
  }, [id]);

  // Public-safe OG/social metadata for a PUBLIC + PUBLISHED opportunity (mirrors backend
  // /api/og/recruitment/{id}). og:image = the dynamic branded 1200x630 recruitment card.
  // Reverted on unmount. NETWORK_ONLY / non-public opportunities never emit an og:image.
  useEffect(() => {
    if (!r) return;
    const isPublic = r.status === "PUBLISHED" && r.visibility === "PUBLIC";
    if (!isPublic) return;
    const prevTitle = document.title;
    const site = publicSiteUrl();
    const url = `${site}/recruitment/${r.id}`;
    const loc = r.is_remote ? "Remote" : [r.city, r.province].filter(Boolean).join(", ");
    const orgName = r.organization?.name || r.recruiter?.name;
    const bits = [r.employment_type_label, loc, orgName].filter(Boolean);
    const desc = ((bits.length ? bits.join(" · ") + ". " : "") + (r.description || "")).slice(0, 200);
    const img = `${site}/api/og/recruitment/${r.id}/card.png`;
    document.title = r.title;
    const set = (k, v, prop = true) => { if (!v) return; const a = prop ? "property" : "name"; let el = document.head.querySelector(`meta[${a}="${k}"]`); if (!el) { el = document.createElement("meta"); el.setAttribute(a, k); document.head.appendChild(el); } el.setAttribute("content", v); };
    set("og:title", r.title); set("og:description", desc); set("og:type", "website"); set("og:url", url);
    set("og:image", img); set("og:image:width", "1200"); set("og:image:height", "630");
    set("twitter:card", "summary_large_image", false); set("twitter:title", r.title, false);
    set("twitter:description", desc, false); set("twitter:image", img, false);
    return () => { document.title = prevTitle; };
  }, [r]);

  const openShare = () => setShareOpen(true);

  if (r === undefined) return <div className="min-h-screen bg-white"><PublicHeader /><div className="flex items-center justify-center py-32 text-slate-400" data-testid="recruitment-loading"><Loader2 className="h-6 w-6 animate-spin" /></div></div>;
  if (r === null) return (
    <div className="min-h-screen bg-white flex flex-col"><PublicHeader /><Seo robots="noindex,follow" />
      <div className="flex-1 flex flex-col items-center justify-center py-32 px-6 text-center" data-testid="recruitment-not-found">
        <Briefcase className="h-10 w-10 text-slate-300 mb-3" />
        <h1 className="font-display text-2xl font-semibold text-[#0F2C59]">Opportunity not available</h1>
        <p className="mt-2 text-sm text-slate-500 max-w-md">This recruitment opportunity is unavailable or has been closed.</p>
        <Link to="/marketplace?mode=recruitment" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F2C59] hover:underline"><ArrowLeft className="h-4 w-4" /> Back to opportunities</Link>
      </div>
    </div>
  );

  const loc = r.is_remote ? "Remote" : [r.city, r.province].filter(Boolean).join(", ");
  const orgName = r.organization?.name || r.recruiter?.name;
  const arrangement = r.is_remote ? "Remote" : (loc ? "On-site / Hybrid" : "");
  const recruiterLinkable = r.recruiter?.profile_published && r.recruiter?.public_profile_path;
  const orgLinkable = r.organization?.slug;
  // JobPosting JSON-LD is emitted ONLY where the opportunity genuinely qualifies (a
  // conventional employment type, a real description, a hiring organization, a posting date
  // and a location or explicit remote scope). Commission-only, team-recruitment and
  // accreditation opportunities are NOT job postings, so we omit recruitment-specific
  // JSON-LD rather than fabricating fields. Mirrors backend /api/og/recruitment/{id}.
  const GOOGLE_EMP = { FULL_TIME: "FULL_TIME", PART_TIME: "PART_TIME", CONTRACT: "CONTRACTOR", INTERNSHIP: "INTERN" };
  const empType = GOOGLE_EMP[r.employment_type];
  const physicalLoc = r.is_remote ? "" : [r.city, r.province].filter(Boolean).join(", ");
  const desc = (r.description || "").trim();
  const jobEligible = empType && orgName && r.first_published_at && (r.is_remote || physicalLoc) && desc.length >= 30;
  const jsonLd = jobEligible ? {
    "@context": "https://schema.org", "@type": "JobPosting", title: r.title,
    description: desc, datePosted: r.first_published_at, employmentType: empType,
    hiringOrganization: { "@type": "Organization", name: orgName },
    url: `${publicSiteUrl()}/recruitment/${r.id}`,
    ...(r.is_remote
      ? { jobLocationType: "TELECOMMUTE", applicantLocationRequirements: { "@type": "Country", name: "Philippines" } }
      : { jobLocation: { "@type": "Place", address: { "@type": "PostalAddress", addressLocality: physicalLoc, addressCountry: "PH" } } }),
  } : undefined;
  const externalMode = r.application_mode === "EXTERNAL_LINK";

  // Structured facts — only rendered when data is present (no empty placeholders).
  const facts = [
    r.role_category && { label: "Role", value: r.role_category },
    r.employment_type_label && { label: "Opportunity type", value: r.employment_type_label },
    loc && { label: "Location", value: loc },
    arrangement && { label: "Work arrangement", value: arrangement },
    r.compensation_text && { label: "Compensation", value: r.compensation_text },
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-white" data-testid="recruitment-detail">
      <Seo title={`${r.title} — Recruitment | PropertyConnect`} description={(r.description || r.title).slice(0, 160)} canonical={`/recruitment/${r.id}`} jsonLd={jsonLd} />
      <PublicHeader />
      <div className="max-w-[1000px] mx-auto px-4 sm:px-6 pt-6">
        <Link to="/marketplace?mode=recruitment" data-testid="recruitment-back" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-[#0F2C59]"><ArrowLeft className="h-4 w-4" /> Opportunities</Link>
      </div>
      <div className="max-w-[1000px] mx-auto px-4 sm:px-6 pt-4">
        {r.cover_image ? (
          <AdaptiveMediaFrame
            src={r.cover_image}
            alt={r.title}
            policy="preservation"
            tone="light"
            context="detail"
            rounded="rounded-2xl"
            loading="eager"
            data-testid="recruitment-hero-cover"
          />
        ) : (
          <div className="relative w-full h-40 sm:h-48 rounded-2xl overflow-hidden" data-testid="recruitment-hero-fallback">
            <RecruitmentFallback title={r.title} org={orgName} typeLabel={r.employment_type_label} variant="hero" />
          </div>
        )}
      </div>

      <div className="max-w-[1000px] mx-auto px-4 sm:px-6 pt-6 pb-12">
        <span className="inline-block text-[11px] font-bold uppercase tracking-wide px-2.5 py-0.5 rounded-full bg-[#C79A3B] text-white">{r.employment_type_label}</span>
        <h1 data-testid="recruitment-title" className="mt-3 font-display text-2xl sm:text-3xl font-bold text-[#0F2C59]">{r.title}</h1>
        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-600">
          {r.organization?.name ? (
            orgLinkable ? (
              <Link to={`/org/${r.organization.slug}`} data-testid="recruitment-org-link" className="flex items-center gap-1.5 hover:text-[#0F2C59]">
                <Building2 className="h-4 w-4 text-slate-400" /><span>{r.organization.name}</span>
                {r.organization.is_verified && <BadgeCheck className="h-4 w-4 text-emerald-600" />}
                {r.organization.is_partner && <ShieldCheck className="h-4 w-4 text-[#C79A3B]" />}
              </Link>
            ) : (
              <span className="flex items-center gap-1.5"><Building2 className="h-4 w-4 text-slate-400" />{r.organization.name}
                {r.organization.is_verified && <BadgeCheck className="h-4 w-4 text-emerald-600" />}
                {r.organization.is_partner && <ShieldCheck className="h-4 w-4 text-[#C79A3B]" />}
              </span>
            )
          ) : r.recruiter?.name && (
            recruiterLinkable ? (
              <Link to={r.recruiter.public_profile_path} data-testid="recruitment-recruiter-link" className="flex items-center gap-1.5 hover:text-[#0F2C59]"><Building2 className="h-4 w-4 text-slate-400" />{r.recruiter.name}</Link>
            ) : <span className="flex items-center gap-1.5"><Building2 className="h-4 w-4 text-slate-400" />{r.recruiter.name}</span>
          )}
          {loc && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-slate-400" />{loc}</span>}
          {r.compensation_text && <span className="flex items-center gap-1.5 font-medium text-slate-800"><Wallet className="h-4 w-4 text-slate-400" />{r.compensation_text}</span>}
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          {externalMode ? (
            <a href={r.external_apply_url} target="_blank" rel="noopener noreferrer" data-testid="recruitment-apply-external">
              <Button className="rounded-xl bg-[#0F2C59] hover:bg-[#0F2C59]/90"><Send className="h-4 w-4 mr-1.5" /> Apply / Express Interest</Button>
            </a>
          ) : (
            <Button data-testid="recruitment-apply-btn" onClick={() => setApplyOpen(true)} className="rounded-xl bg-[#0F2C59] hover:bg-[#0F2C59]/90"><Send className="h-4 w-4 mr-1.5" /> Apply / Express Interest</Button>
          )}
          <Button data-testid="recruitment-ask-btn" onClick={() => setAskOpen(true)} variant="outline" className="rounded-xl border-[#0F2C59]/25 text-[#0F2C59]"><MessageCircle className="h-4 w-4 mr-1.5" /> Ask about this</Button>
          <Button data-testid="recruitment-share-btn" onClick={openShare} variant="outline" className="rounded-xl border-[#0F2C59]/25 text-[#0F2C59]"><Share2 className="h-4 w-4 mr-1.5" /> Share</Button>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {r.description && (
              <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="recruitment-description">
                <h2 className="text-lg font-semibold text-[#0F2C59] mb-2">About this opportunity</h2>
                <p className="text-[15px] leading-relaxed text-slate-600 whitespace-pre-line">{r.description}</p>
              </section>
            )}
            {facts.length > 0 && (
              <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="recruitment-facts">
                <h2 className="text-lg font-semibold text-[#0F2C59] mb-3">Opportunity details</h2>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                  {facts.map((f) => (
                    <div key={f.label} className="min-w-0">
                      <dt className="text-xs uppercase tracking-wide text-slate-400 font-semibold">{f.label}</dt>
                      <dd className="text-[15px] text-slate-700 mt-0.5">{f.value}</dd>
                    </div>
                  ))}
                </dl>
              </section>
            )}
          </div>
          <aside className="space-y-6">
            {(r.recruiter?.name || r.organization?.name) && (
              <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="recruitment-recruiter">
                <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Posted by</h2>
                <div className="flex items-center gap-3">
                  {r.recruiter?.avatar
                    ? <img src={r.recruiter.avatar} alt="" className="h-11 w-11 rounded-full object-cover" />
                    : <span className="h-11 w-11 rounded-full bg-[#0F2C59]/8 flex items-center justify-center text-[#0F2C59] font-bold">{(r.recruiter?.name || "?").charAt(0)}</span>}
                  <div className="min-w-0">
                    {recruiterLinkable
                      ? <Link to={r.recruiter.public_profile_path} className="font-semibold text-[#0F2C59] hover:underline truncate block">{r.recruiter?.name}</Link>
                      : <span className="font-semibold text-slate-800 truncate block">{r.recruiter?.name}</span>}
                    {r.recruiter?.role_label && <span className="text-[13px] text-slate-500">{r.recruiter.role_label}</span>}
                  </div>
                </div>
                {r.organization?.name && (
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2">Organization</h3>
                    {orgLinkable
                      ? <Link to={`/org/${r.organization.slug}`} className="flex items-center gap-1.5 font-medium text-[#0F2C59] hover:underline"><Building2 className="h-4 w-4" />{r.organization.name}{r.organization.is_verified && <BadgeCheck className="h-4 w-4 text-emerald-600" />}{r.organization.is_partner && <ShieldCheck className="h-4 w-4 text-[#C79A3B]" />}</Link>
                      : <span className="flex items-center gap-1.5 font-medium text-slate-800"><Building2 className="h-4 w-4 text-slate-400" />{r.organization.name}</span>}
                  </div>
                )}
              </section>
            )}
          </aside>
        </div>
      </div>
      <ApplyDialog open={applyOpen} onOpenChange={setApplyOpen} rid={r.id} isAuthed={!!user} />
      <AskDialog open={askOpen} onOpenChange={setAskOpen} rid={r.id} isAuthed={!!user} />
      <ShareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        url={`${publicSiteUrl()}/recruitment/${r.id}`}
        title={r.title}
        text={`${r.title}${orgName ? ` — ${orgName}` : ""}`}
        entityId={r.id}
        entityType="recruitment"
        channels={["facebook", "messenger", "whatsapp", "viber", "copy"]}
        heading="Share this opportunity"
        subtitle="Share this recruitment opportunity with your network. The link opens the public opportunity page on PropertyConnect."
      />
    </div>
  );
}
